import { useEffect, useRef } from "react";

/**
 * Poll while the tab is visible; stop completely while it is not.
 *
 * WHY THIS EXISTS
 * ---------------
 * pages/Routers.js refreshed the router list every 5 s with no `document.hidden`
 * check, no backoff and no pause on blur. components/Sidebar.js polls the same
 * endpoint every 30 s and is mounted inside AdminLayout, so it runs on every
 * admin page, and Routers.js carried a third 30 s timer besides. Measured
 * 15 Aug 2026: one admin leaving the Routers tab open in the background is
 * 720 + 120 + 120 ≈ 960 `/api/routers` calls an hour, indefinitely.
 *
 * The cost was never MikroTik load — that list is served from stored state and
 * makes no router calls. It was backend: every authenticated request also fired
 * `UPDATE users SET last_active_at`, which is how a 17-row table absorbed
 * 414,109 updates (see backend/utils/activityPing.js).
 *
 * PAUSE, NOT BACKOFF
 * The investigation suggested backing off to 30 s while hidden. Pausing outright
 * is strictly better *because* the callback also fires the moment the tab becomes
 * visible again: a hidden tab then costs nothing at all, and the user still never
 * reads stale data, since the refresh lands before they can look. Backing off
 * would keep paying for a tab nobody is watching AND still show data up to 30 s
 * old at the moment of return.
 *
 * ── RECOVERY TRIGGERS (added 24 Aug 2026) ───────────────────────────────────
 * Pausing on hidden was correct but it was the ONLY thing that could bring a
 * stalled poller back. Three ways to lose freshness had no recovery path at all:
 *
 *   · the browser reconnected after a network drop — nothing refetched, so a
 *     failed poll's damage persisted until the next tick, and a wedged request
 *     persisted forever;
 *   · the operator pressed the global refresh button — `refreshTick` changed
 *     the caller's callback identity, but this hook holds the callback in a REF
 *     and keys its arming effect on [intervalMs], so a new tick invoked
 *     NOTHING. The refresh control was a no-op for every consumer of this hook.
 *   · a router was switched — see the callers.
 *
 * `refreshKey` closes the second: change it and the callback runs now. The
 * `online` listener closes the first. Both are deliberately cheap — one request
 * per real event, never a new timer.
 * See docs/production-findings/ui/ROUTER_PRESENCE_LONG_LIVED_SPA_INVESTIGATION_2026-08-24.md
 *
 * @param {Function} callback   invoked immediately, then on each tick
 * @param {number} intervalMs   0 / null disables the interval (single load only)
 * @param {object} [options]
 * @param {*} [options.refreshKey]      changing this value forces an immediate run
 * @param {boolean} [options.refetchOnReconnect=true]  run when the browser regains network
 */
export default function useVisiblePolling(callback, intervalMs, options = {}) {
  const { refreshKey, refetchOnReconnect = true } = options;

  // Held in a ref so a caller passing an inline arrow does not restart the timer
  // on every render — that would reset the interval continuously and, with an
  // immediate first call, poll far faster than requested.
  const savedCallback = useRef(callback);
  useEffect(() => { savedCallback.current = callback; }, [callback]);

  useEffect(() => {
    let timerId = null;

    const run = () => { savedCallback.current?.(); };

    const stop = () => {
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    // Always clears before arming. A hidden/visible flap would otherwise stack
    // one interval per transition, so the tab would poll FASTER the more it was
    // switched away from — the opposite of the point.
    const start = () => {
      stop();
      if (intervalMs > 0) timerId = setInterval(run, intervalMs);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        run();     // refresh first, so the tab is current before it is read
        start();
      }
    };

    // A reconnect means we may have missed everything since the drop, so the
    // only safe assumption is that our copy is wrong. Refetch rather than wait
    // out the remaining interval. Guarded on visibility so a background tab
    // coming back online still costs nothing.
    const onOnline = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      run();
      start();   // re-arm so the next tick is a full interval after the recovery
    };

    run();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (refetchOnReconnect && typeof window !== "undefined") {
      window.addEventListener("online", onOnline);
    }

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (refetchOnReconnect && typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
      }
    };
  }, [intervalMs, refetchOnReconnect]);

  // Manual refresh. Separate from the arming effect above so a new key does not
  // tear down and rebuild the interval; the mount value is skipped so this never
  // duplicates the immediate first call.
  const firstKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === firstKeyRef.current) return;
    firstKeyRef.current = refreshKey;
    savedCallback.current?.();
  }, [refreshKey]);
}
